import type {
  BlockContentRecord,
  PartialBlockWithLegacyTableContent,
} from './block-content-types';

/**
 * Detects table content that BlockNote can render: at least one row with cells.
 */
function hasRenderableTableContent(
  content: BlockContentRecord | undefined,
): content is BlockContentRecord {
  if (!content || content.type !== 'tableContent') {
    return false;
  }

  const rows = content.rows;
  if (!Array.isArray(rows) || rows.length === 0) {
    return false;
  }

  return rows.some((row) => Array.isArray(row.cells) && row.cells.length > 0);
}

/**
 * Removes empty table rows and cells from persisted table content while keeping
 * the original tableContent container shape.
 */
function normalizeTableContent(
  content: BlockContentRecord,
): BlockContentRecord {
  const rows = Array.isArray(content.rows) ? content.rows : [];
  const normalizedRows = rows.flatMap((row) => {
    const rowCells = Array.isArray(row.cells) ? row.cells : [];
    const normalizedCells = rowCells.filter(Boolean);

    if (normalizedCells.length === 0) {
      return [];
    }

    return [
      {
        ...row,
        cells: normalizedCells,
      },
    ];
  });

  return {
    ...content,
    rows: normalizedRows,
  };
}

function isTableContentRecord(
  content: PartialBlockWithLegacyTableContent['content'],
): content is BlockContentRecord {
  return (
    typeof content === 'object' &&
    content !== null &&
    !Array.isArray(content) &&
    'type' in content &&
    content.type === 'tableContent'
  );
}

/**
 * Reads table content from the current BlockNote location or the legacy
 * top-level field used by older persisted documents.
 */
export function resolveTableContentFromBlock(
  block: PartialBlockWithLegacyTableContent,
): BlockContentRecord | undefined {
  if (isTableContentRecord(block.content)) {
    return block.content;
  }

  if (block.tableContent?.type === 'tableContent') {
    return block.tableContent;
  }

  return undefined;
}

/**
 * Returns normalized table content only when the table still has renderable
 * cells; callers use undefined to replace empty table shells.
 */
export function normalizeRenderableTableContent(
  content: BlockContentRecord | undefined,
): BlockContentRecord | undefined {
  if (!hasRenderableTableContent(content)) {
    return undefined;
  }

  return normalizeTableContent(content);
}
